<template>
    <section class="setup-chat-card" :aria-labelledby="headingId">
        <div class="masthead">
            <div class="artwork" aria-hidden="true">
                <SetupCalendarTile class="size-9" />
                <SetupPerspectiveTile class="size-9" />
                <SetupProfileTile class="size-9" />
            </div>
            <BaseButton theme="tertiary" size="sm" icon-only aria-label="Dismiss coaching setup offer" :disabled="isDismissing" @click="handleDismiss">
                <i class="bi bi-x-lg" aria-hidden="true"></i>
            </BaseButton>
        </div>
        <h3 :id="headingId" class="headline">The better I know you, the more I can help.</h3>
        <p class="description">A few details about you help me offer advice that fits your working life.</p>
        <div class="action">
            <BaseLink :href="offer.url" class="base-btn-primary base-btn-sm" @click="logOfferEvent(AmplitudeEventNames.CARD_CLICKED)">
                {{ offer.completed_count === 0 ? "Start setup" : "Continue setup" }}
            </BaseLink>
        </div>
        <BaseNotification v-if="dismissError" theme="error" hide-dismiss class="mt-4" text="Couldn't dismiss right now. Please try again." />
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { GuidedSetupNewChatOffer } from "~vue/ChatDetail/types";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import SetupCalendarTile from "~vue/icons/SetupCalendarTile.vue";
import SetupPerspectiveTile from "~vue/icons/SetupPerspectiveTile.vue";
import SetupProfileTile from "~vue/icons/SetupProfileTile.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { onMounted, useId } from "vue";

const headingId = useId();

const { offer, coachingSessionUuid = null } = defineProps<{
    offer: GuidedSetupNewChatOffer;
    coachingSessionUuid?: string | null;
}>();
const emit = defineEmits<{ dismiss: [] }>();
const {
    execute: persistDismiss,
    error: dismissError,
    isFetching: isDismissing,
} = useFetch("/api/accounts/ui-interactions", { immediate: false }).post({ interaction_type: "guided_setup_new_chat_offer_dismissed" }).json();

function logOfferEvent(eventName: AmplitudeEventNames): void {
    void logUserInteraction(
        eventName,
        {
            card_type: "coaching_setup",
            page: "chat",
            coaching_setup_completed_count: offer.completed_count,
            coaching_setup_step_count: offer.total,
            coaching_setup_next_step: offer.next_step,
            card_offer_sequence_number: offer.offer_sequence_number,
            card_dismiss_count: offer.dismiss_count,
        },
        coachingSessionUuid ?? undefined,
    );
}

onMounted(() => logOfferEvent(AmplitudeEventNames.CARD_VIEWED));

async function handleDismiss(): Promise<void> {
    await persistDismiss();
    if (dismissError.value) {
        return;
    }
    logOfferEvent(AmplitudeEventNames.CARD_CLOSED);
    emit("dismiss");
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .setup-chat-card {
        @apply rounded-xl border border-border bg-surface-raised p-5;
    }
    .masthead {
        @apply mb-3 flex items-center justify-between gap-3;
    }
    .artwork {
        @apply flex shrink-0 items-center gap-1;
    }
    .headline {
        @apply display-small-serif text-pretty text-ink;
    }
    .description {
        @apply body-small mt-2 max-w-112 text-ink-muted;
    }
    .action {
        @apply mt-5 flex;
    }
}
</style>
